<template>
  <BContainer
    class="px-0"
    fluid
  >
    <BRow
      class="py-2 mb-3"
    >
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="gridEvents.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Event Date"
          label-for="filterEventDate"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterEventDate"
            v-model="gridEvents.filter.created_at"
            debounce="1000"
            type="date"
            :min="formEventValidation.eventDate.min"
            :max="formEventValidation.eventDate.max"
            :disabled="gridEvents.busy"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click.prevent="createEvent"
        >
          Record Event
        </BButton>
      </BCol>
    </BRow>

    <BRow
      v-if="gridEvents.busy"
      class="gap-y-6 mb-3"
    >
      <BCol
        cols="12"
      >
        <div class="text-center py-5">
          <BIcon
            icon="stopwatch"
            font-scale="5"
            animation="cylon"
          />
          <p class="h3 py-2">
            <strong>Loading . . .</strong>
          </p>
        </div>
      </BCol>
    </BRow>

    <BRow
      v-else-if="Number(gridEvents.totalRows) <= 0 && !gridEvents.busy"
    >
      <BCol
        cols="12"
        class="text-center py-5"
      >
        <BIcon
          icon="calendar"
          font-scale="5"
        />
        <p class="h3 py-2">
          <strong>No events found</strong>
        </p>
      </BCol>
    </BRow>

    <BRow
      v-else
      class="gap-y-6 mb-3"
    >
      <BCol
        v-for="(event, index) in gridEvents.rows"
        :key="index"
        cols="12"
        lg="6"
      >
        <Event
          :details="{
            id: event.id,
            province_name: event?.province ? (event.province?.province_name || '') : '',
            municipality_name: event?.municipality ? (event.municipality.municipality_name || '') : '',
            customer_name: event?.customer ? `${event.customer?.first_name || ''} ${event.customer?.last_name || ''}` : '',
            remarks: event.remarks,
            event_date: event.event_date
          }"
          :attachment-path="event.attachment_path || ''"
          :attachments="event.attachments"
          :loading="gridEvents.busy"
          @show:attachment="attachmentIndex => openPictures(event, attachmentIndex)"
          @more:attachments="() => viewEvent(event)"
        >
          <template #footer>
            <div class="d-flex justify-content-between">
              <div>
                {{ dateTimeShortFormatter(event.created_at) }}
              </div>
              <div class="d-flex gap-2">
                <BButton
                  size="sm"
                  variant="warning"
                  @click.prevent="editEvent(event, true)"
                >
                  View
                </BButton>
                <BButton
                  v-if="isEventEditable(event)"
                  size="sm"
                  variant="primary"
                  @click.prevent="editEvent(event, false)"
                >
                  Edit
                </BButton>
                <BButton
                  size="sm"
                  variant="outline-danger"
                  @click.prevent="deleteEvent(event)"
                >
                  Delete
                </BButton>
              </div>
            </div>
          </template>
        </Event>
      </BCol>
    </BRow>

    <BRow>
      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-md-0 mb-2"
      >
        <BFormSelect
          v-model="gridEvents.perPage"
          :options="gridEvents.pageOptions"
          :disabled="gridEvents.busy"
          class="w-100 w-md-75px"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1"
      >
        <BPagination
          v-model="gridEvents.currentPage"
          :total-rows="gridEvents.totalRows"
          :per-page="gridEvents.perPage"
          :disabled="gridEvents.busy"
          class="mb-0"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>
    </BRow>

    <BModal
      ref="modalTaskEvent"
      size="lg"
      title="Task"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      @shown="eventModalShown"
      @hidden="eventModalHidden"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-event-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-lines', {
                  'text-danger fat fa-shake': vTabEventDetails()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabEventDetails()
                }]"
              >
                Details
              </b>
            </div>
          </template>

          <ValidationObserver
            ref="formEvent"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="activity_id"
                    name="activity"
                    rules="required"
                  >
                    <BFormGroup
                      label="Activity"
                      label-for="fActivityName"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fActivityName"
                        v-model="formEvent.activity_name"
                        type="text"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="event_date"
                    name="date"
                    rules="required"
                  >
                    <BFormGroup
                      label="Event Date"
                      label-for="fEventDate"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fEventDate"
                        v-model="formEvent.event_date"
                        type="date"
                        :min="formEventValidation.eventDate.min"
                        :max="formEventValidation.eventDate.max"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || state.viewing"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="customer_id"
                    name="customer"
                    :rules="{
                      required: Number(task.rules.customer) === 1
                    }"
                  >
                    <BFormGroup
                      label="Customer"
                      label-for="fCustomer"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.customer"
                        input-id="fCustomer"
                        label="customer_name"
                        append-to-body
                        placeholder="select customer here"
                        :options="options.customers"
                        :disabled="[
                          fetching.customers,
                          state.busy,
                          state.viewing,
                          Number(task.rules.customer) === 0
                        ].some(isTrue => isTrue)"
                        :loading="fetching.customers"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchCustomers"
                      >
                        <template #option="{ customer_name, mobile_number, address }">
                          <div class="d-flex flex-column flex-md-row py-1">
                            <div class="flex-grow-1 d-flex align-items-center">
                              <div class="mr-2">
                                <BAvatar
                                  :text="String(customer_name).charAt(0)"
                                  variant="primary"
                                  size="50px"
                                />
                              </div>
                              <div class="d-flex flex-column">
                                <div
                                  class="text-break text-wrap font-weight-bolder"
                                  v-text="customer_name"
                                />
                                <strong class="mb-2">
                                  {{ mobile_number }}
                                </strong>
                                <div
                                  v-if="address"
                                  class="text-break text-wrap text-muted small"
                                >
                                  {{ address }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                        <template #selected-option="{ customer_name }">
                          <div class="d-flex flex-column flex-md-row">
                            <div class="flex-grow-1 d-flex align-items-center">
                              <div class="mr-2">
                                <BAvatar
                                  :text="String(customer_name).charAt(0)"
                                  variant="primary"
                                  size="28px"
                                />
                              </div>
                              <div
                                class="w-200px w-lg-300px text-truncate"
                                v-text="customer_name"
                              />
                            </div>
                          </div>
                        </template>
                        <template #no-options="">
                          no available customer
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="province_id"
                    name="province"
                    rules="required"
                  >
                    <BFormGroup
                      label="Province"
                      label-for="fProvince"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.province"
                        input-id="fProvince"
                        label="province_name"
                        append-to-body
                        placeholder="select province here"
                        :options="options.provinces"
                        :disabled="fetching.provinces || state.busy || state.viewing"
                        :loading="fetching.provinces"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchProvinces"
                      >
                        <template #option="{ province_name, region }">
                          <div
                            class="text-break text-wrap"
                            v-text="province_name"
                          />
                          <div
                            v-if="region"
                            class="text-muted small"
                            v-text="region?.region_name"
                          />
                        </template>
                        <template #selected-option="{ province_name }">
                          <div
                            class="w-200px w-lg-300px text-truncate"
                            v-text="province_name"
                          />
                        </template>
                        <template #no-options="">
                          no available province
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="municipality_id"
                    name="municipality"
                    rules="required"
                  >
                    <BFormGroup
                      label="Municipality"
                      label-for="fMunicipality"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.municipality"
                        input-id="fMunicipality"
                        label="municipality_name"
                        append-to-body
                        placeholder="select municipality here"
                        :options="options.municipalities"
                        :disabled="state.busy || fetching.municipalities || fetching.provinces || state.viewing"
                        :loading="fetching.municipalities"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchMunicipalities"
                      >
                        <template #option="{ municipality_name }">
                          <div
                            class="text-break text-wrap"
                            v-text="municipality_name"
                          />
                        </template>
                        <template #selected-option="{ municipality_name }">
                          <div
                            class="w-200px w-lg-300px text-truncate"
                            v-text="municipality_name"
                          />
                        </template>
                        <template #no-options="">
                          no available municipality
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="remarks"
                    name="remarks"
                    rules="required|max:1000"
                  >
                    <BFormGroup
                      label="Remarks"
                      label-for="fRemarks"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <template #label>
                        <span class="d-flex justify-content-between">
                          Remarks
                          <span>
                            {{ formEvent.remarks ? formEvent.remarks.length : 0 }} / 1000
                          </span>
                        </span>
                      </template>
                      <BFormTextarea
                        id="fRemarks"
                        v-model="formEvent.remarks"
                        placeholder="enter remarks here"
                        rows="7"
                        max-rows="9999"
                        maxlength="1000"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || state.viewing"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>
              </BRow>
            </form>
          </ValidationObserver>
        </BTab>

        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-paperclip', {
                  'text-danger fat fa-shake': vTabAttachments()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabAttachments()
                }]"
              >
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :loading="state.busy"
            :valid-id="formEvent.id"
            :document-types="task.documents"
            :path="formEvent.attachment_path"
            :files="formEvent.attachments"
            :deleted-files="formEvent.deleted_files"
            :show-uploader="!state.viewing"
            @files:updated="updateEventAttachments"
          />
        </BTab>
        <BTab>
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-map-marker-alt" />
              <b class="is-tab-active ml-1">
                Location
              </b>
            </div>
          </template>
          <GeoLocationMap
            ref="geoLocationMap"
            :enabled="!state.viewing"
            :coords="formEvent.geolocation"
            @updated:coords="updateEventGeolocation"
          />
        </BTab>
      </BTabs>
      <template #modal-footer="{ cancel }">
        <div class="w-100 d-flex justify-content-end align-items-center gap-2">
          <BButton
            v-if="!state.viewing"
            variant="primary"
            :disabled="state.busy"
            @click.prevent="validateEvent"
          >
            {{ state.editing ? 'Update' : 'Record' }} Event
          </BButton>
          <BButton

            variant="outline-dark"
            :disabled="state.busy"
            @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
          >
            {{ state.editing && !state.viewing ? 'Discard' : 'Close' }}
          </BButton>
        </div>
      </template>
    </BModal>

    <Preview
      ref="modalPreview"
    />
  </BContainer>
</template>
<script>
import QUERY from 'lodash'
import MOMENT from 'moment'

import Event from '@/views/fso/task-events/components/Event.vue'
import Attachment from '@/views/fso/task-events/components/Attachment.vue'
import Preview from '@/views/fso/activity-recordings/components/Preview.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MProvinces from '@/mixins/locations/provinces'
import MMunicipalities from '@/mixins/locations/municipalities'
import MCustomers from '@/mixins/accounts/customers'

import { SFSOTaskEvent } from '@/services/fso'
import GeoLocationMap from '@/components/custom/GeoLocationMap.vue'

// import Products from '@/views/fso/task-events/components/Products.vue'

export default {
  name: 'FSOTaskEvents',

  components: {
    Event,
    // Products,
    Attachment,
    Preview,
    GeoLocationMap
  },

  mixins: [
    MxMisc,
    MxFormatter,
    MxImageUrl,
    MProvinces,
    MMunicipalities,
    MCustomers
  ],

  props: {
    task: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        activity_id: '',
        activity_name: '',
        assignee_id: '',
        customer_per_event: 0,
        target_hectare: 0,
        budget_per_event: 0,
        crop_id: '',
        crop_name: '',
        schedule: '',
        conversion_rate: 0.00,
        customers_bought_count: 0,
        events_count: 0,
        customers_attended_count: 0,
        total_actual_hectares: 0,
        total_actual_costs: 0,
        total_actual_sales: 0,
        event_plan: 0,
        total_customer_plan: 0,
        total_hectare_plan: 0,
        total_budget_plan: 0,
        total_sales_plan: 0,
        rules: {
          customer: 0,
          actual_hectares: 0,
          customer_attended: 0,
          actual_costs: 0,
          customer_bought: 0,
          actual_sales: 0,
          customer_bookings: 0,
          booking_sales: 0
        },
        documents: [],
        active: 1
      })
    }
  },

  data () {
    return {
      geoLocationId: null,
      state: {
        busy: false,
        editing: false,
        viewing: false
      },
      formEventValidation: {
        eventDate: {
          min: MOMENT(this.task.schedule).startOf('month').format('YYYY-MM-DD'),
          max: MOMENT(this.task.schedule).endOf('month').format('YYYY-MM-DD')
        },
        created_at: ''
      },
      formEvent: {
        id: '',
        activity_id: this.task.activity_id,
        activity_name: this.task.activity_name || '',
        task_id: this.task.id,
        customer_id: '',
        event_date: '',
        province_id: '',
        municipality_id: '',
        remarks: '',
        attachment_path: '',
        attachments: [],
        deleted_files: [],
        products: [],
        active: 1,
        geolocation: {
          latitude: 0,
          longitude: 0
        }
      },
      gridEvents: {
        busy: false,
        filter: {
          search: '',
          task: this.task.id,
          activity: this.task.activity_id,
          created_at: ''
        },
        filterOn: [],
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: []
      }
    }
  },

  watch: {

    'selected.customer' (customer) {
      this.formEvent.customer_id = customer?.id || ''

      // this.selected.province = ''
      // this.$nextTick(() => {
      //   this.selected.province = QUERY.find(this.options.provinces, {
      //     id: customer?.province_id || 0
      //   }) || ''
      //   this.editable.municipality = customer?.municipality_id || ''
      // })
    },

    'selected.province' (province) {
      this.formEvent.province_id = province?.id || ''
    },

    'selected.municipality' (municipality) {
      this.formEvent.municipality_id = municipality?.id || ''
    },

    'gridEvents.filter.search' () {
      this.gridProvider()
    },

    'gridEvents.filter.created_at' () {
      this.gridProvider()
    },

    'gridEvents.currentPage' () {
      this.gridProvider()
    },

    'gridEvents.perPage' () {
      this.gridProvider()
    }
  },

  mounted () {
    this.gridProvider()
  },

  methods: {

    vTabEventDetails () {
      if (this.$refs.formEvent) {
        const formEventErrors = this.$refs.formEvent.errors
        return Object.keys(formEventErrors).some(
          errorKey => formEventErrors[errorKey]?.length > 0
        )
      }

      return false
    },

    vTabProducts () {
      // if (this.$refs.formEvent) {
      //   const formEventErrors = this.$refs.formEvent.errors
      //   return Object.keys(formEventErrors).some(
      //     errorKey => formEventErrors[errorKey]?.length > 0
      //   )
      // }

      return false
    },

    vTabAttachments () {
      if (this.$refs.attachmentUploader) {
        const formDocument = this.$refs.attachmentUploader.$refs.formDocument

        if (formDocument) {
          return Object.keys(formDocument.errors).some(
            errorKey => formDocument.errors[errorKey]?.length > 0
          )
        }
      }

      return false
    },

    isEventEditable (taskEvent) {
      const today = MOMENT()
      const createdAt = MOMENT(taskEvent.created_at)

      if (createdAt.add(72, 'hours').isSameOrBefore(today)) {
        return false
      }

      return true
    },

    async gridProvider () {
      this.gridEvents.busy = true

      const payload = {
        page: this.gridEvents.currentPage,
        per_page: this.gridEvents.perPage,
        sort: this.gridEvents.sortBy,
        sort_desc: this.gridEvents.sortDesc,
        filter_text: this.gridEvents.filter.search,
        task_id: this.gridEvents.filter.task,
        activity_id: this.gridEvents.filter.activity,
        event_date: this.gridEvents.filter.created_at
      }

      return SFSOTaskEvent.get(payload).then(
        ({ data }) => {
          this.gridEvents.totalRows = data.total_rows
          this.gridEvents.rows = data.items
          return data.items
        }
      ).catch(() => {
        this.gridEvents.totalRows = 0
        this.gridEvents.rows = []
        return []
      }).finally(() => {
        this.gridEvents.busy = false
      })
    },

    updateEventAttachments (attachment) {
      this.formEvent = Object.assign(this.formEvent, {
        attachment_path: attachment.attachment_path,
        attachments: attachment.attachments,
        deleted_files: attachment.deleted_files
      })
    },

    updateEventProducts (products) {
      this.formEvent.products = products
    },

    updateEventGeolocation (geolocation) {
      this.formEvent = Object.assign(this.formEvent, {
        geolocation
      })
    },

    openPictures (activity, index) {
      this.$refs.modalPreview.show(activity.attachments, activity.attachment_path, index)
    },

    createEvent () {
      this.state.viewing = false
      this.state.editing = false

      this.formEventValidation.created_at = ''

      const dateFormat = 'YYYY-MM-DD'
      const currentMonth = `${this.task.schedule}-01`
      const today = MOMENT()
      const month = MOMENT(MOMENT(currentMonth).format(dateFormat))

      this.formEventValidation.eventDate = Object.assign(this.formEventValidation.eventDate, {
        min: month.startOf('month').format(dateFormat),
        max: month.endOf('month').format(dateFormat)
      })

      if (month.isSame(today, 'month')) {
        this.formEvent.event_date = today.format(dateFormat)
      }

      const activityRules = Object.assign({
        customer: 0
      }, this.task?.rules || {})

      this.formEvent = Object.assign(this.formEvent, {
        id: '',
        task_id: this.task.id,
        activity_id: this.task.activity_id,
        activity_name: this.task.activity_name,
        customer_per_event: this.task.customer_per_event,
        customer_id: '',
        province_id: '',
        municipality_id: '',
        remarks: '',
        attachment_path: '',
        attachments: [],
        deleted_files: [],
        products: [],
        active: 1,
        geolocation: {
          latitude: 0,
          longitude: 0
        }
      })

      this.getProvinces(this)

      if (Number(activityRules?.customer) === 1) {
        this.getCustomers(this)
      } else {
        this.selected.customer = ''
      }

      this.$refs.modalTaskEvent.show()
    },

    editEvent (event, viewOnly = false) {
      const today = MOMENT()
      const createdAt = MOMENT(event.created_at)

      if (createdAt.add(72, 'hours').isSameOrBefore(today) && !viewOnly) {
        return this.swalInvalid('You can no longer edit this event. Editing is only allowed within 72 hours from the time it was created.')
      }

      this.state.viewing = viewOnly
      this.state.editing = true

      this.formEventValidation.created_at = event.created_at

      this.editable.province = event.province_id
      this.editable.municipality = event.municipality_id

      this.formEvent = Object.assign(this.formEvent, {
        id: Number(event.id),
        task_id: this.task.id,
        activity_id: this.task.activity_id,
        activity_name: this.task.activity_name || '',
        customer_per_event: this.task?.customer_per_event || 1,
        event_date: event.event_date,
        customer_id: event.customer_id,
        province_id: event.province_id,
        municipality_id: event.municipality_id,
        remarks: event.remarks,
        attachment_path: event.attachment_path,
        attachments: event.attachments || [],
        deleted_files: [],
        products: event?.purchases || [],
        geolocation: {
          latitude: event?.geolocation?.latitude || 0,
          longitude: event?.geolocation?.longitude || 0
        },
        active: event.active
      })

      this.getCustomers(this).finally(() => {
        this.selected.customer = QUERY.find(this.options.customers, {
          id: event?.customer_id
        }) || ''
      })

      this.getProvinces(this).finally(() => {
        this.selected.province = QUERY.find(this.options.provinces, {
          id: event?.province_id
        }) || ''
      })

      this.$refs.modalTaskEvent.show()
    },

    validateEvent () {
      this.$refs.formEvent.validate().then(
        validEvent => {
          if (!validEvent) {
            return this.swalInvalid()
          }

          const today = MOMENT()
          const createdAt = MOMENT(this.formEventValidation.created_at)

          if (createdAt.add(72, 'hours').isSameOrBefore(today) && !this.state.viewing) {
            return this.swalInvalid('You can no longer edit this event. Editing is only allowed within 72 hours from the time it was created.')
          }

          const noAttachments = this.formEvent.attachments.length === 0

          const hasRequiredDocuments = this.task.documents.filter(
            documentType => Number(documentType.required) === 1
          ).map(
            documentType => ({
              name: documentType.document_type_name,
              uploaded: this.formEvent.attachments.some(
                attachment => Number(attachment.document_type_id) === Number(documentType.id)
              )
            })
          )

          if (noAttachments || !hasRequiredDocuments.every(doc => doc.uploaded)) {
            const requiredDocuments = hasRequiredDocuments.filter(doc => !doc.uploaded).map(doc => doc.name).join(', ')

            this.$refs.attachmentUploader.$refs.formDocument.setErrors({
              attachments: `Please upload at least one attachment for ${requiredDocuments}.`
            })

            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Event?</h6>' : '<h6>Record this Event?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Record',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putEvent() : this.postEvent()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    if (this.$refs.attachmentUploader) {
                      this.$refs.attachmentUploader.$refs.formDocument.setErrors(message.errors)
                    }

                    this.$refs.formEvent.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postEvent () {
      return SFSOTaskEvent.post(this.formEvent).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalTaskEvent.hide()

            this.gridProvider()
          })

          return data
        }
      )
    },

    async putEvent () {
      return SFSOTaskEvent.put(this.formEvent).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalTaskEvent.hide()

            this.gridProvider()
          })

          return data
        }
      )
    },

    async deleteEvent (event) {
      return this.swalConfirm({
        html: `<div>
          <h5>Delete this Event?</h5>
          <b class="text-danger">This action cannot be undone.</b>
        </div>`,
        // remarksRequired: true,
        // remarksMaxLength: 1000,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Keep',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          const payload = {
            id: event.id,
            remarks: ''
          }

          return SFSOTaskEvent.delete(payload).then(
            ({ data }) => {
              this.swalSuccess(data.message).then(() => {
                this.gridProvider()
              })
              return data
            }
          )
        }
      })
    },

    eventModalHidden () {
      this.detachArrowScroller('.nav-event-tabs')

      if (this.geoLocationId) {
        navigator.geolocation.clearWatch(this.geoLocationId)
        this.geoLocationId = null
      }
    },

    eventModalShown () {
      this.attachArrowScroller('.nav-event-tabs')

      this.$refs.geoLocationMap.initMap()
    }
  }
}
</script>
